import React, {useCallback, useMemo, useState} from "react";
import { Box, Container, Divider, Grid, makeStyles, Typography } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { CustomButton, CustomChip, CustomIconButton, ResponsiveDialog, StreamlineIcon } from "@hapinest/theme";
import RealEstateDealKey from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/real-estate/deals/real-estate-deal-key.svg";
import CommonFileDouble2 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/files-folders/common-files/common-file-double-2.svg";
import PropTypes from "prop-types";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CopyToClipboard from "react-copy-to-clipboard";
import CustomRichText from "../text/CustomRichText";
import CustomRichTextFromMarkdown from "../text/CustomRichTextFromMarkdown";
import { useI18N, useLocation, useLocalizedUrl } from "../../../common/common";
import { useBreakpoints, useKeycloakFunctions } from "../../../hooks";
import RatingStar
    from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/social-medias-rewards-rating/rating/rating-star.svg";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import LoginTrialOrMemberDialog from "../../dialog/membership/LoginTrialOrMemberDialog";
import { MEMBERSHIP } from "../../../common/routes";

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
    },
    placeholder: {
        zIndex: "-1",
        position: "absolute",
        height: "100% !important",
        width: "100% !important",
        objectFit: "cover"
    },
    paper: {
        border: "1px solid " + theme.palette.borders.line.light,
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    image: {
        paddingBottom: theme.spacing(2),
        [theme.breakpoints.up("sm")]: {
            paddingBottom: 0,
            paddingRight: theme.spacing(2),
        },
        "&>img": {
            width: "100%",
            height: "100%",
            borderRadius: `${theme.spacing(0.5)}px`,
            objectFit: "cover",
        },
    },
    typographyOverline: {
        textTransform: "uppercase",
        color: theme.palette.secondary.main + " !important",
    },
    chip: {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    description: {
        "& .MuiTypography-h3, .MuiTypography-body1": {
            marginBottom: theme.spacing(3)
        },
    },
    divider: {
        backgroundColor: theme.palette.borders.line.light,
        margin: theme.spacing(3, 0, 2)
    },
    verticalAlignTextBottom: {
        verticalAlign: "text-bottom"
    },
    perkIcon: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.secondary["700"],
        borderRadius: theme.spacing(0.5),
        "&>i": {
            color: theme.palette.common.white,
        },
    },
    infoMessage: {
        ...theme.typography.infoMessage,
        marginBottom: theme.spacing(2)
    },
    starIcon: {
        color: theme.palette.grey["400"],
        paddingRight: theme.spacing(1)
    }
}));

const PartnerOfferings = ({data}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {isExtraSmallScreen, isSmallScreen} = useBreakpoints();
    const {sendCustomEvent, eventNames} = useAnalytics();
    const {
        authenticated,
        isProspect,
        isTrialMember,
    } = useKeycloakFunctions();
    const {getComposePagePath} = useLocation();
    const {getLocalizedUrl} = useLocalizedUrl();

    const [isDialogOpen, setDialogOpen] = useState(false);
    const [selectedOffering, setSelectedOffering] = useState(null);
    const [isLoginTrialOrProspectDialogOpen, setLoginTrialOrProspectDialogOpen] = useState(false);

    const handleDialogOpen = (item) => {
        setSelectedOffering(item);
        setDialogOpen(true);

        trackAnalyticsEvent(eventNames.PARTNER_BOOK_NOW, item);
    };

    const handleDialogClose = () => {
        setSelectedOffering(null);
        setDialogOpen(false);
    };

    const handleClickAction = (item, isRestricted) => {
        if(isRestricted === false) {
            handleDialogOpen(item);
        } else {
            if(!authenticated) {
                setLoginTrialOrProspectDialogOpen(true);
            } else {
                const localisedUrl = getLocalizedUrl(getComposePagePath(MEMBERSHIP));
                window.open(localisedUrl, "_blank");
            }
        }
    };

    const handleCopy = () => {
        trackAnalyticsEvent(eventNames.PARTNER_COPY_CODE, selectedOffering);
    };

    const handleClickFinalAction = () => {
        selectedOffering?.promoUrl && window.open(selectedOffering.promoUrl, "_blank");
        handleDialogClose();

        trackAnalyticsEvent(eventNames.PARTNER_FIND_STAY, selectedOffering);
    };

    const trackAnalyticsEvent = useCallback((eventName, offering) => {
        if(eventName && sendCustomEvent && offering) {
            sendCustomEvent({
                event_name: eventName,
                [eventName]: {
                    resort_name: offering?.name,
                    resort_id: "PRT_" + offering?.id,
                    resort_country: offering?.countries?.toLowerCase(),
                    resort_region: "--"
                }
            });
        }
    }, [sendCustomEvent]);

    const isRestricted = useMemo(() => {
        return !authenticated || isProspect() || isTrialMember();
    }, [authenticated, isProspect(), isTrialMember()]);

    return (
        <>
            <Box bgcolor={data?.bgColor}>
                <Container
                    maxWidth="lg"
                    className={classes.container}
                >
                    {data ? (
                        <>
                            <Box
                                mb={5}
                            >
                                <Typography
                                    variant="h1"
                                    align="center"
                                >
                                    {data.title}
                                </Typography>
                            </Box>

                            {data.offerings?.map((item, index) => (
                                <Box
                                    className={classes.paper}
                                    p={2}
                                    key={index}
                                >
                                    <Grid
                                        container
                                    >
                                        <Grid
                                            item
                                            sm={12}
                                            md={5}
                                            className={classes.image}
                                        >
                                            <LazyLoadImage
                                                alt={item.image?.alt}
                                                src={item.image?.url}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            md={7}
                                        >
                                            <Box
                                                mt={2}
                                            >
                                                <Typography
                                                    variant="h1"
                                                >
                                                    {item.name}
                                                </Typography>
                                                <Typography
                                                    variant="overline"
                                                    gutterBottom
                                                    className={classes.typographyOverline}
                                                >
                                                    {item.countries}
                                                </Typography>
                                                <Box pt={1.25}>
                                                    <Grid
                                                        item
                                                    >
                                                        {item.tags.map((tag, index) => (
                                                            tag !== "-" && (
                                                                <CustomChip
                                                                    key={tag + index}
                                                                    label={tag}
                                                                    className={classes.chip}
                                                                />
                                                            )
                                                        ))}
                                                    </Grid>
                                                </Box>
                                                <Box
                                                    mt={8}
                                                    mb={item.subperks && isRestricted === false ? 3 : 10}
                                                    className={classes.description}
                                                >
                                                    <CustomRichText document={item.description} />
                                                </Box>
                                            </Box>

                                            {item.subperks && isRestricted === false && (
                                                <Box
                                                    mt={2}
                                                >
                                                    <Typography
                                                        className={classes.infoMessage}
                                                    >
                                                        {item.subperksTitle}
                                                    </Typography>
                                                    {item.subperks.map((description, index) => (
                                                        <Box
                                                            key={index}
                                                            display="flex"
                                                            alignItems="center"
                                                            mb={2}
                                                        >
                                                            <StreamlineIcon className={classes.starIcon}>
                                                                <RatingStar />
                                                            </StreamlineIcon>
                                                            <Typography className={classes.descriptionText}>
                                                                {description}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            <Divider className={classes.divider} />
                                            <Box
                                                display="flex"
                                                flexDirection="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Box>
                                                    <Box
                                                        mr={1}
                                                        display="inline-block"
                                                        className={classes.perkIcon}
                                                    >
                                                        <StreamlineIcon>
                                                            <RealEstateDealKey/>
                                                        </StreamlineIcon>
                                                    </Box>

                                                    <Box
                                                        display="inline-block"
                                                        className={classes.verticalAlignTextBottom}
                                                    >
                                                        {isRestricted === false ? (
                                                            <CustomRichTextFromMarkdown
                                                                markdownString={item.mainPerk}
                                                            />
                                                        ) : (
                                                            i18n("view.partner_offering.available_members_only")
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <CustomButton
                                                        variant="contained"
                                                        disableElevation
                                                        onClick={() => handleClickAction(item, isRestricted)}
                                                    >
                                                        {isRestricted === false ? i18n("view.booking.confirmation_success.book_now") : i18n("common.label.learn_more")}
                                                    </CustomButton>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                        </>
                    ) : (
                        <Skeleton className={classes.placeholder}/>
                    )}
                </Container>
            </Box>

            <ResponsiveDialog
                open={isDialogOpen}
                handleClose={handleDialogClose}
                hasCloseButton
                isFullScreen={false}
                content={(
                    <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="column"
                        textAlign="center"
                    >
                        <img
                            src="/images/best-out-of-hapimag-page-discount-pictogram.svg"
                            alt={i18n("view.partner_offering.how_to_book_with_discount")}
                            width={100}
                            height={100}
                        />
                        <Box
                            mt={3}
                        >
                            <Typography
                                variant="h1"
                            >
                                {i18n("view.partner_offering.how_to_book_with_discount")}
                            </Typography>
                        </Box>

                        <Box
                            mt={3}
                        >
                            {selectedOffering?.bookingInformation ? (
                                <CustomRichText document={selectedOffering.bookingInformation} />
                            ) : (
                                <Typography
                                    variant="body1"
                                >
                                    {selectedOffering?.promoCode ? i18n("view.partner_offering.copy_promo_code") : i18n("view.partner_offering.click_here_access_member_discounts")}
                                </Typography>
                            )}
                            {selectedOffering?.promoCode && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    mt={4}
                                >
                                    <Box>
                                        <Typography
                                            variant="h3"
                                        >
                                            {selectedOffering.promoCode}
                                        </Typography>
                                    </Box>
                                    <Box
                                        ml={2}
                                    >
                                        <CopyToClipboard
                                            text={selectedOffering.promoCode}
                                            onCopy={handleCopy}
                                        >
                                            {(isExtraSmallScreen() || isSmallScreen()) ? (
                                                <CustomIconButton
                                                    variant={"outlined"}
                                                    color={"black"}
                                                    className={classes.floatRight}
                                                >
                                                    <StreamlineIcon><CommonFileDouble2/></StreamlineIcon>
                                                </CustomIconButton>
                                            ) : (
                                                <CustomButton
                                                    variant={"outlined"}
                                                    color={"black"}
                                                    size={"medium"}
                                                    startIcon={<StreamlineIcon><CommonFileDouble2/></StreamlineIcon>}
                                                >
                                                    {i18n("common.label.copy")}
                                                </CustomButton>
                                            )}
                                        </CopyToClipboard>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
                actions={(
                    <Box
                        alignItems="center"
                    >
                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            justify="center"
                        >
                            <Grid
                                item
                                xs={12}
                                sm="auto"
                            >
                                <CustomButton
                                    onClick={handleClickFinalAction}
                                    variant="contained"
                                    fullWidth={isExtraSmallScreen()}
                                >
                                    {i18n("view.partner_offering.find_stay")}
                                </CustomButton>
                            </Grid>
                        </Grid>
                        <Box
                            mt={4}
                            mx={2}
                            textAlign="center"
                        >
                            <Typography
                                variant="caption"
                            >
                                {i18n("view.partner_offering.note_direct_contractual_partner")}
                            </Typography>
                        </Box>
                    </Box>
                )}
            />

            {!authenticated &&
                <LoginTrialOrMemberDialog
                    isOpen={isLoginTrialOrProspectDialogOpen}
                    onClose={() => setLoginTrialOrProspectDialogOpen(false)}
                />
            }
        </>
    );
};

PartnerOfferings.propTypes = {
    data: PropTypes.object
};

export default PartnerOfferings;
